import { html, raw } from '../lib/html.js';
import { reviews } from '../services/reviews.js';
import { qrSvg } from '../lib/qr.js';
import { maintenances, setMeterUnit } from '../services/maintenances.js';
import { dismissSetup, history, pill, sectionTitle, setupCard, setupLater, stats, timeline, upcomingList } from './maintenance-ui.js';
import { AUTH_STATUS, accessRequests, authorizations, countdown, fmtDateTime, spacedCode } from '../services/authorizations.js';
import { busy, confirm, openPhoto, prompt, toast, toastError, toastSuccess } from '../lib/ui.js';
import { CLAIM_STATUS, REJECTION_TEXT, RELATION, VERIFIED_OWNER, categoryOf, vehicleId, vehicles } from '../services/vehicles.js';
import { Page, go } from './base.js';
import { bindDocumentConfirmation, documentConfirmation, bindUpload, uploadBox } from './vehicle-upload.js';
import { vehicleHero, vehicleLine } from './vehicle-hero.js';

const dateFmt = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });
const fmt = (v) => (v ? dateFmt.format(new Date(v)) : '');

/**
 * /vehicles/:vehicleId[?aba=historico|acesso]
 *   com acesso   → topo fixo (placa, nome, km e revisões) + três abas na mesma tela:
 *                  Resumo (o que vence, último serviço, autorizar oficina) · Histórico (por ano, com filtro)
 *                  · Acesso (oficinas e pessoa autorizada); dados do veículo abrem no topo (placa ou link)
 *   sem acesso   → andamento do envio do CRLV-e: enviar, em análise, recusado (reenviar)
 */
const TABS = [
  { key: 'resumo', label: 'Resumo' },
  { key: 'historico', label: 'Histórico' },
  { key: 'acesso', label: 'Acesso' },
];
class VehiclePage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/home" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Veículo</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding surface-page" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  setup() {
    this.addEventListener('ionViewWillLeave', () => clearInterval(this.salePoll));
    this.$('ion-refresher').addEventListener('ionRefresh', async (e) => {
      await this.load();
      e.target.complete();
    });
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) this.action(el.dataset.action, el);
    });
  }

  /** O ion-router passa :vehicleId como propriedade; o pathname garante o primeiro carregamento. */
  get vid() {
    if (this.vehicleId) return String(this.vehicleId);
    const m = location.pathname.match(/\/vehicles\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  onEnter() {
    this.data = null;
    this.openMaint = null;
    this.maintVersions = {};
    const aba = new URLSearchParams(location.search).get('aba');
    this.tab = TABS.some((t) => t.key === aba) ? aba : 'resumo';
    this.histFilter = 'all';
    this.allUpcoming = false;
    this.allReviews = false;
    this.legendOpen = false;
    this.pastOpen = false;
    this.dataOpen = false;
    this.load();
  }

  async load() {
    if (!this.data) this.$('.body').innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      this.data = await vehicles.get(this.vid);
      const rel = this.data.access?.relation;
      const owner = VERIFIED_OWNER.includes(rel);
      // dono (inclusive o provisório) e pessoa autorizada liberam oficinas; só o dono comprovado compartilha e vende
      this.auths = owner || rel === 'AUTHORIZED_USER' || rel === 'PROVISIONAL_OWNER' ? (await authorizations.list(this.vid)).items : null;
      // empresas que pediram este veículo pela placa (quem libera oficinas também responde aos pedidos)
      this.requests = this.auths ? await accessRequests.received().then((r) => r.items.filter((x) => x.vehicle.id === this.vid)).catch(() => []) : [];
      this.share = owner ? await vehicles.share(this.vid).catch(() => null) : null;
      this.sale = owner ? await vehicles.transfer(this.vid).catch(() => null) : null;
      this.watchSale();
      this.maint = this.data.access ? await maintenances.ownerList(this.vid) : null;
      this.toReview = this.data.access ? await reviews.pending().then((r) => r.items.filter((x) => x.vehicle.id === this.vid)).catch(() => []) : [];
      this.render();
    } catch (err) {
      this.$('.body').innerHTML = html`
        <div class="center">
          <ion-icon name="alert-circle-outline" class="empty-icon"></ion-icon>
          <p>${err.status === 404 ? 'Este veículo não está na sua garagem.' : err.message}</p>
          <ion-button fill="outline" data-action="home">Voltar ao início</ion-button>
        </div>`.value;
    }
  }

  /**
   * Venda em andamento: confere a cada 5 s. Se o comprador aceitar com esta tela aberta, o veículo já não é
   * mais do vendedor: avisa e volta para o início (não deixa os dados do veículo na tela).
   */
  watchSale() {
    clearInterval(this.salePoll);
    const t = this.sale?.transfer;
    if (t?.status !== 'PENDING') return;
    const vid = this.vid;
    this.salePoll = setInterval(async () => {
      try {
        const r = await vehicles.transferStatus(vid, t.id);
        if (this.vid !== vid || r.transfer.status === 'PENDING') return;
        clearInterval(this.salePoll);
        if (r.transfer.status === 'ACCEPTED') {
          toastSuccess(`Venda concluída: ${r.buyer?.firstName ?? 'o comprador'} aceitou. O veículo saiu da sua garagem.`);
          return go('/home', 'root');
        }
        this.load();
      } catch {
        /* tenta de novo no próximo ciclo */
      }
    }, 5000);
  }

  render() {
    const { vehicle: v, access, claims } = this.data;
    const claim = claims[0] ?? null; // mais recente
    const nickname = access?.nickname || claim?.submitted?.nickname || '';
    const model = [v.brand, v.model].filter(Boolean).join(' ');
    const chip = access ? RELATION[access.relation] : CLAIM_STATUS[claim?.status];

    setMeterUnit(v.meterUnit);
    const hero = vehicleHero({
      plate: v.plateFormatted,
      serial: v.serialNumber,
      category: v.category,
      title: nickname || model || vehicleId(v),
      subtitle: nickname ? vehicleLine(v) : [v.modelYear, v.color].filter(Boolean).join(' · '),
      status: chip ? pill(chip.color, chip.label) : '',
      editable: !!access,
      plateAction: access ? 'vehicle-data' : '',
      compact: !!access,
      extra: html`${access?.companyName ? html`<p class="hero-note">Em nome de ${access.companyName}</p>` : ''}${access
        ? html`<button type="button" class="link-btn hero-data-btn" data-action="vehicle-data" aria-expanded="${this.dataOpen ? 'true' : 'false'}"><ion-icon name="document-text-outline"></ion-icon>${this.dataOpen ? 'Ocultar dados do veículo' : 'Dados do veículo'}</button>`
        : ''}`,
    });

    if (!access) {
      this.$('.body').innerHTML = html`${hero}${pending(claim, v.category)}`.value;
      if (claim?.status === 'PENDING_UPLOAD') {
        if (claim.documentReady) bindDocumentConfirmation(this, claim, () => this.load());
        else bindUpload(this, claim.id, () => this.uploaded());
      }
      return;
    }

    const m = this.maint;
    const kmAction = html`<button type="button" class="link-btn stat-action" data-action="km-update">${m?.currentKm === null ? 'Informar' : 'Atualizar'}</button>`;
    const tab = this.tab;
    const body = tab === 'historico' ? this.historyTab() : tab === 'acesso' ? this.accessTab(v, access) : this.summaryTab(v);

    this.$('.body').innerHTML = html`
      ${hero}
      ${this.dataOpen ? vehicleData(v, access) : ''}
      ${m ? stats(m, { kmAction, compact: true }) : ''}
      <div class="vtabs" role="tablist" aria-label="Seções do veículo">
        ${TABS.map(
          (t) => html`<button type="button" role="tab" class="${t.key === tab ? 'on' : ''}" aria-selected="${t.key === tab ? 'true' : 'false'}" data-action="tab" data-tab="${t.key}">${t.label}</button>`,
        )}
      </div>
      <div class="vtab-body" role="tabpanel">${body}</div>`.value;
  }

  /** Resumo: o que pede ação agora. Avaliação pendente, lembretes, próximas revisões, último serviço e acesso de oficina. */
  summaryTab(v) {
    const m = this.maint;
    const setup = m?.setup?.available && m.setup.questions > 0 ? m.setup : null;
    const rows = m ? timeline(m.items, m.initialData ?? []) : [];
    const last = rows[0];
    const open = (this.auths ?? []).filter((a) => a.status === 'ACTIVE' || a.status === 'PENDING');
    const canAuthorize = this.auths !== null;
    return html`
      ${(this.data.ownershipClaims ?? []).map((c) => ownershipClaimCard(c))}
      ${this.data.provisional ? provisionalCard(this.data.claims[0] ?? null) : ''}
      ${(this.allReviews ? this.toReview ?? [] : (this.toReview ?? []).slice(0, 1)).map((it) => html`
        <button type="button" class="panel row tappable review-ask" data-action="review" data-id="${it.maintenanceId}">
          <span class="row-icon"><ion-icon name="star-outline"></ion-icon></span>
          <div class="row-main">
            <span class="row-title">Como foi o serviço ${it.workshop?.name ? `de ${it.workshop.name}` : 'da empresa'}?</span>
            <span class="row-sub">${it.services.join(', ')} · avalie até ${new Date(it.until).toLocaleDateString('pt-BR')}</span>
          </div>
          <ion-icon class="chevron" name="chevron-forward"></ion-icon>
        </button>`)}
      ${!this.allReviews && (this.toReview ?? []).length > 1
        ? html`<button type="button" class="more-reviews" data-action="reviews-all">+ ${this.toReview.length - 1} ${this.toReview.length - 1 === 1 ? 'outro serviço' : 'outros serviços'} para avaliar</button>`
        : ''}
      ${setup && !setupLater(v.id) ? setupCard({ vid: v.id, questions: setup.questions }) : ''}
      ${m ? upcomingList(m.upcoming, { limit: this.allUpcoming ? 0 : 3 }) : ''}
      ${canAuthorize
        ? html`
          ${sectionTitle('Empresas', open.length ? html`<button type="button" class="link-btn" data-action="tab" data-tab="acesso">Gerenciar</button>` : '')}
          ${open.length
            ? html`<div class="panel">${authRows(open, this.shownQr)}</div>`
            : html`
              <div class="panel cta-panel">
                <div class="row">
                  <span class="row-icon"><ion-icon name="qr-code-outline"></ion-icon></span>
                  <div class="row-main">
                    <span class="row-title">Vai levar o veículo a uma empresa?</span>
                    <span class="row-sub">Gere um código para ela ver o histórico e registrar o serviço.</span>
                  </div>
                </div>
                <div class="panel-foot"><ion-button expand="block" data-action="authorize"><ion-icon slot="start" name="qr-code-outline"></ion-icon>Autorizar empresa</ion-button></div>
              </div>`}`
        : ''}
      ${m
        ? last
          ? html`
            ${sectionTitle('Último registro', rows.length > 1 ? html`<button type="button" class="link-btn" data-action="tab" data-tab="historico">Ver histórico (${rows.length})</button>` : '')}
            ${history(last.m ? [last.m] : [], { initial: last.d ? [last.d] : [], open: this.openMaint, versions: this.maintVersions, title: false, legendMode: 'none', ownerChanges: m.ownerChanges ?? [] })}`
          : history([], {
              emptyText: setup ? 'Os serviços de empresas autorizadas aparecem aqui. Informe as últimas trocas para começar os lembretes.' : undefined,
              emptyAction: setup && setupLater(v.id) ? html`<ion-button expand="block" fill="outline" class="empty-cta" data-action="setup-start">Informar as últimas trocas</ion-button>` : '',
            })
        : ''}`;
  }

  /** Histórico completo: filtro por tipo de serviço e grupos por ano. */
  historyTab() {
    const m = this.maint;
    if (!m) return '';
    const initial = m.initialData ?? [];
    const all = timeline(m.items, initial);
    if (!all.length) {
      const setup = m.setup?.available && m.setup.questions > 0;
      return history([], {
        emptyText: setup ? 'Os serviços de empresas autorizadas aparecem aqui. Informe as últimas trocas para começar os lembretes.' : undefined,
        emptyAction: setup ? html`<ion-button expand="block" fill="outline" class="empty-cta" data-action="setup-start">Informar as últimas trocas</ion-button>` : '',
      });
    }
    // filtro: tipos que aparecem no histórico (os mais frequentes primeiro) + "Informado pelo dono"
    const freq = new Map();
    for (const it of m.items.flatMap((x) => x.items)) freq.set(it.typeName, (freq.get(it.typeName) ?? 0) + 1);
    const types = [...freq.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
    const f = this.histFilter;
    const items = f === 'all' ? m.items : f === 'owner' ? [] : m.items.filter((x) => x.items.some((i) => i.typeName === f));
    const init = f === 'all' || f === 'owner' ? initial : initial.filter((d) => d.items.some((i) => i.typeName === f));
    const chip = (key, label) => html`<button type="button" class="chip ${f === key ? 'on' : ''}" data-action="hist-filter" data-filter="${key}" aria-pressed="${f === key ? 'true' : 'false'}">${label}</button>`;
    const showFilter = all.length >= 4 && (types.length > 1 || initial.length);
    return html`
      ${showFilter
        ? html`<div class="chips-row" role="group" aria-label="Filtrar por serviço">
            ${chip('all', 'Todos')}
            ${types.map((t) => chip(t, t))}
            ${initial.length ? chip('owner', 'Informado pelo dono') : ''}
          </div>`
        : ''}
      ${items.length || init.length
        ? history(items, { initial: init, open: this.openMaint, versions: this.maintVersions, byYear: true, title: false, legendMode: 'toggle', legendOpen: this.legendOpen, ownerChanges: f === 'all' ? m.ownerChanges ?? [] : [] })
        : html`<p class="hint">Nenhum registro de ${f} ainda.</p>`}`;
  }

  /** Acesso: quem pode ver e mexer no veículo (oficinas e pessoa autorizada). */
  accessTab(v, access) {
    return html`
      ${access.relation === 'AUTHORIZED_USER' ? authorizedNotice(this.data.owner) : ''}
      ${authSection(this.auths, this.shownQr, this.pastOpen, this.requests)}
      ${shareSection(this.share)}
      ${saleSection(this.sale)}`;
  }

  async uploaded() {
    toastSuccess('Documento enviado! Avisaremos quando a análise terminar.');
    await this.load();
  }

  /** Pessoa autorizada: convidar, cancelar convite, remover acesso (dono) e sair (autorizado). */
  async shareAction(name) {
    try {
      if (name === 'share-invite') {
        const email = await prompt({
          header: 'Compartilhar veículo',
          message: 'E-mail da pessoa que vai usar o veículo. Ela vê o histórico, recebe os lembretes e pode autorizar empresas. O veículo continua no seu nome.',
          placeholder: 'email@exemplo.com',
          maxlength: 254,
          confirmText: 'Enviar convite',
        });
        if (!email) return;
        await vehicles.invite(this.vid, email.trim().toLowerCase());
        toastSuccess('Convite enviado. Vale por 7 dias.');
      }
      if (name === 'share-cancel') {
        if (!(await confirm({ header: 'Cancelar convite?', confirmText: 'Cancelar convite', cancelText: 'Voltar', destructive: true }))) return;
        await vehicles.cancelInvite(this.vid);
        toast('Convite cancelado.');
      }
      if (name === 'share-remove') {
        const who = this.share?.authorized?.name?.split(' ')[0] ?? 'A pessoa';
        if (!(await confirm({ header: 'Remover acesso?', message: `${who} deixa de ver o veículo e de autorizar empresas. As empresas já autorizadas continuam até você revogar.`, confirmText: 'Remover', cancelText: 'Voltar', destructive: true }))) return;
        await vehicles.removeAuthorized(this.vid);
        toast('Acesso removido.');
      }
      if (name === 'share-leave') {
        if (!(await confirm({ header: 'Sair deste veículo?', message: 'O veículo sai da sua garagem. Para voltar, o dono precisa convidar você de novo.', confirmText: 'Sair', cancelText: 'Voltar', destructive: true }))) return;
        await vehicles.leave(this.vid);
        toast('Você saiu do veículo.');
        return go('/home', 'root');
      }
    } catch (err) {
      return toastError(err.message);
    }
    return this.load();
  }

  /** Outra pessoa enviou o CRLV-e deste veículo: "Sim, vendi" (sai da garagem) ou "Não reconheço" (o admin confere). */
  async answerClaim(name, docId) {
    const c = (this.data.ownershipClaims ?? []).find((x) => x.id === docId);
    try {
      if (name === 'claim-confirm') {
        const sure = await confirm({
          header: 'Confirmar a venda?',
          message: `O veículo sai da sua garagem agora e passa para ${c?.firstName ?? 'o comprador'}. A pessoa autorizada e as empresas também perdem o acesso. O histórico vai junto com o veículo.`,
          confirmText: 'Sim, vendi',
          cancelText: 'Voltar',
          destructive: true,
        });
        if (!sure) return;
        await vehicles.confirmSale(this.vid, docId);
        toastSuccess('Venda confirmada. O veículo saiu da sua garagem.');
        return go('/home', 'root');
      }
      const note = await prompt({
        header: 'Não reconhece este envio?',
        message: 'Conte em poucas palavras (opcional). Nossa equipe confere os documentos antes de qualquer mudança, e o veículo continua com você enquanto isso.',
        placeholder: 'Ex.: não vendi o veículo',
        maxlength: 300,
        confirmText: 'Contestar',
      });
      if (note === null) return;
      await vehicles.contestClaim(this.vid, docId, note || undefined);
      toast('Contestação enviada. Vamos conferir com cuidado.');
    } catch (err) {
      return toastError(err.message);
    }
    return this.load();
  }

  async editNickname() {
    const current = this.data?.access?.nickname ?? '';
    const value = await prompt({
      header: 'Apelido do veículo',
      message: 'Um nome para reconhecer o veículo na sua garagem. Deixe em branco para usar o modelo.',
      value: current,
      placeholder: 'Ex.: CARRO DE TRABALHO',
      maxlength: 40,
      uppercase: true,
    });
    if (value === null || value === current) return;
    try {
      await vehicles.setNickname(this.vid, value || undefined);
      toastSuccess(value ? 'Apelido salvo.' : 'Apelido removido.');
      await this.load();
    } catch (err) {
      toastError(err.message);
    }
  }

  async action(name, el) {
    const { vehicle: v, claims } = this.data ?? {};
    const claim = claims?.[0];
    if (name === 'home') return go('/home', 'root');
    if (name === 'review') return go(`/avaliar/${el.dataset.id}`);
    if (name === 'nickname') return this.editNickname();
    if (name === 'photo') return openPhoto(el.dataset.url);
    if (name === 'tab') {
      this.tab = el.dataset.tab;
      this.openMaint = null;
      this.render();
      // mantém as abas à vista ao trocar (o conteúdo novo começa logo abaixo delas)
      const tabs = this.$('.vtabs');
      const content = this.$('ion-content');
      if (tabs && content?.scrollToPoint) {
        const el2 = await content.getScrollElement();
        if (el2.scrollTop > tabs.offsetTop) content.scrollToPoint(0, tabs.offsetTop - 8, 200);
      }
      return;
    }
    if (name === 'vehicle-data') {
      this.dataOpen = !this.dataOpen;
      return this.render();
    }
    if (name === 'hist-filter') {
      this.histFilter = el.dataset.filter;
      this.openMaint = null;
      return this.render();
    }
    if (name === 'sell') return go(`/vehicles/${this.vid}/vender`);
    if (name === 'proof') return go(`/vehicles/${this.vid}/comprovar`);
    if (name === 'claim-confirm' || name === 'claim-contest') return this.answerClaim(name, el.dataset.id);
    if (name === 'reviews-all') {
      this.allReviews = true;
      return this.render();
    }
    if (name === 'upcoming-all') {
      this.allUpcoming = true;
      return this.render();
    }
    if (name === 'legend') {
      this.legendOpen = !this.legendOpen;
      return this.render();
    }
    if (name === 'past-auths') {
      this.pastOpen = !this.pastOpen;
      return this.render();
    }
    if (name === 'toggle') {
      this.openMaint = this.openMaint === el.dataset.id ? null : el.dataset.id;
      return this.render();
    }
    if (name === 'versions') {
      try {
        this.maintVersions = { ...(this.maintVersions ?? {}), [el.dataset.id]: (await maintenances.ownerVersions(this.vid, el.dataset.id)).items };
        return this.render();
      } catch (err) {
        return toastError(err.message);
      }
    }
    if (name === 'setup-start' || name === 'initial-edit') return go(`/vehicles/${this.vid}/lembretes`);
    if (name === 'km-update') return go(`/vehicles/${this.vid}/km`);
    if (name === 'setup-later') {
      dismissSetup(this.vid);
      return this.render();
    }
    if (name === 'authorize') return go(`/vehicles/${this.vid}/autorizar`);
    if (name === 'requests') return go('/pedidos-acesso');
    if (name.startsWith('share-')) return this.shareAction(name);
    if (name === 'show-qr') {
      this.shownQr = this.shownQr === el.dataset.id ? null : el.dataset.id;
      return this.render();
    }
    if (name === 'revoke-auth') {
      const a = this.auths?.find((x) => x.id === el.dataset.id);
      const pendingCode = a?.status === 'PENDING';
      const sure = await confirm({
        header: pendingCode ? 'Cancelar código?' : 'Revogar autorização?',
        message: pendingCode ? 'O código deixa de funcionar.' : `${a?.workshop?.name ?? 'A empresa'} perde o acesso ao veículo agora.`,
        confirmText: pendingCode ? 'Cancelar código' : 'Revogar',
        cancelText: 'Voltar',
        destructive: true,
      });
      if (!sure) return;
      try {
        await authorizations.revoke(this.vid, a.id);
        toast(pendingCode ? 'Código cancelado.' : 'Autorização revogada.');
      } catch (err) {
        toastError(err.message);
      }
      return this.load();
    }
    if (name === 'view-file') {
      try {
        const { url } = await vehicles.fileUrl(claim.id);
        window.open(url, '_blank');
      } catch (err) {
        toastError(err.message);
      }
    }
    if (name === 'cancel') {
      const sure = await confirm({ header: 'Cancelar envio?', message: 'O veículo sai da sua garagem. Você pode adicionar de novo depois.', confirmText: 'Cancelar envio', cancelText: 'Voltar', destructive: true });
      if (!sure) return;
      try {
        await vehicles.cancelClaim(claim.id);
        toast('Envio cancelado.');
        go('/home', 'root');
      } catch (err) {
        toastError(err.message);
      }
    }
    if (name === 'dismiss') {
      const sure = await confirm({ header: 'Remover da garagem?', message: 'O veículo sai da sua garagem. Se ele for seu, você pode adicionar de novo e enviar outro documento.', confirmText: 'Remover', cancelText: 'Voltar', destructive: true });
      if (!sure) return;
      try {
        await vehicles.dismissClaim(claim.id);
        toast('Veículo removido da garagem.');
        go('/home', 'root');
      } catch (err) {
        toastError(err.message);
      }
    }
    if (name === 'resubmit') {
      await busy(el, async () => {
        try {
          if ((v.category ?? 'LIGHT') === 'LIGHT' && (await vehicles.registrationConfig()).vioEnabled) return go('/vehicles/new?tipo=LIGHT');
          const s = claim.submitted ?? {};
          await vehicles.createClaim({ category: v.category ?? 'LIGHT', ...(v.plate ? { plate: v.plate } : {}), ...(v.renavam ? { renavam: v.renavam } : {}), ...(v.serialNumber ? { serialNumber: v.serialNumber } : {}), ...(s.nickname ? { nickname: s.nickname } : {}) });
          await this.load();
        } catch (err) {
          toastError(err.message);
        }
      });
    }
  }
}

/** Dados do veículo (abre no topo, pela placa ou pelo link "Dados do veículo"). */
function vehicleData(v, access) {
  const kv = (label, value, cls = '') => html`<div class="kv"><span>${label}</span><strong class="${cls}">${value || '—'}</strong></div>`;
  return html`
    <div class="panel vehicle-data">
      <button type="button" class="kv tappable" data-action="nickname">
        <span>Apelido</span>
        <strong>${access.nickname || html`<em class="muted">Adicionar</em>`}</strong>
        <ion-icon name="create-outline" class="kv-icon"></ion-icon>
      </button>
      ${kv('Tipo', `${categoryOf(v).label} · ${categoryOf(v).kind}`)}
      ${v.plateFormatted || categoryOf(v).key !== 'AGRO' ? kv('Placa', v.plateFormatted, 'mono') : ''}
      ${v.renavam || categoryOf(v).key !== 'AGRO' ? kv('RENAVAM', v.renavam, 'mono') : ''}
      ${v.serialNumber ? kv(categoryOf(v).key === 'AGRO' ? 'Nº de série' : 'Chassi / série', v.serialNumber, 'mono') : ''}
      ${kv('Marca e modelo', [v.brand, v.model].filter(Boolean).join(' '))}
      ${kv('Ano modelo', v.modelYear)}
      ${kv('Cor', v.color)}
      ${kv('Na garagem desde', fmt(access.since))}
    </div>`;
}

/** Linhas das oficinas com acesso ativo e dos códigos ainda não usados (com QR para mostrar no balcão). */
function authRows(open, shownQr) {
  return open.map((a) =>
    a.status === 'ACTIVE'
      ? html`
        <div class="row">
          <span class="row-icon success"><ion-icon name="storefront-outline"></ion-icon></span>
          <div class="row-main">
            <span class="row-title">${a.workshop?.name ?? 'Empresa'}</span>
            <span class="row-sub">Acesso até ${fmtDateTime(a.expiresAt)}</span>
          </div>
          <ion-button size="small" fill="clear" color="danger" data-action="revoke-auth" data-id="${a.id}">Revogar</ion-button>
        </div>`
      : html`
        <div class="row">
          <span class="row-icon warning"><ion-icon name="qr-code-outline"></ion-icon></span>
          <div class="row-main">
            <span class="row-title">Código <span class="mono">${spacedCode(a.code)}</span></span>
            <span class="row-sub">${a.workshop ? `Para ${a.workshop.name}` : 'Qualquer empresa'} · vale mais ${countdown(a.codeExpiresAt)}</span>
          </div>
          <ion-button size="small" fill="clear" data-action="show-qr" data-id="${a.id}">${shownQr === a.id ? 'Ocultar' : 'QR'}</ion-button>
          <ion-button size="small" fill="clear" color="medium" data-action="revoke-auth" data-id="${a.id}">Cancelar</ion-button>
        </div>
        ${shownQr === a.id ? html`<div class="qr inline">${raw(qrSvg(a.qr))}</div>` : ''}`,
  );
}

/** Empresas que pediram o veículo pela placa e esperam a resposta (o aceite, com o prazo, é em /pedidos-acesso). */
const requestRows = (requests) =>
  (requests ?? []).map((r) => html`
    <div class="row">
      <span class="row-icon warning"><ion-icon name="time-outline"></ion-icon></span>
      <div class="row-main">
        <span class="row-title">${r.workshop.name} pediu acesso</span>
        <span class="row-sub">Responda até ${fmtDateTime(r.expiresAt)}</span>
      </div>
      <ion-button size="small" fill="clear" data-action="requests">Responder</ion-button>
    </div>`);

function authSection(auths, shownQr, pastOpen, requests) {
  if (!auths) return '';
  const open = auths.filter((a) => a.status === 'ACTIVE' || a.status === 'PENDING');
  const past = auths.filter((a) => a.status !== 'ACTIVE' && a.status !== 'PENDING').slice(0, 10);
  return html`
    ${sectionTitle('Empresas autorizadas')}
    <div class="panel">
      ${requestRows(requests)}
      ${authRows(open, shownQr)}
      ${!open.length && !requests?.length
        ? html`
          <div class="row">
            <span class="row-icon"><ion-icon name="storefront-outline"></ion-icon></span>
            <div class="row-main">
              <span class="row-title">Nenhuma empresa com acesso</span>
              <span class="row-sub">A empresa autorizada vê o histórico e registra os serviços.</span>
            </div>
          </div>`
        : ''}
      <div class="panel-foot">
        <ion-button expand="block" ${open.length ? html`fill="outline"` : ''} data-action="authorize"><ion-icon slot="start" name="qr-code-outline"></ion-icon>${open.length ? 'Autorizar outra empresa' : 'Autorizar empresa'}</ion-button>
      </div>
      ${past.length
        ? html`
          <button type="button" class="row tappable more-row" data-action="past-auths" aria-expanded="${pastOpen ? 'true' : 'false'}">Autorizações anteriores (${past.length})<ion-icon name="${pastOpen ? 'chevron-up' : 'chevron-down'}"></ion-icon></button>
          ${pastOpen
            ? past.map((a) => html`
              <div class="row">
                <div class="row-main">
                  <span class="row-title">${a.workshop?.name ?? 'Código não usado'}</span>
                  <span class="row-sub">${fmtDateTime(a.activatedAt ?? a.createdAt)}</span>
                </div>
                ${pill(AUTH_STATUS[a.status]?.color ?? 'medium', AUTH_STATUS[a.status]?.label ?? a.status)}
              </div>`)
            : ''}`
        : ''}
    </div>`;
}

const fmtShort = (v) => new Date(v).toLocaleDateString('pt-BR');

/** "Você vendeu este veículo?": outra pessoa enviou o CRLV-e e o dono pode confirmar ou contestar. */
const ownershipClaimCard = (c) => html`
  <div class="panel notice warn-card">
    <span class="row-icon warning"><ion-icon name="swap-horizontal-outline"></ion-icon></span>
    <div>
      <strong>Você vendeu este veículo?</strong>
      <p>${c.firstName} enviou o documento deste veículo no nome dele. Responda até ${fmtShort(c.respondBy)}; depois disso, nossa equipe analisa o documento.</p>
      <div class="notice-actions">
        <ion-button size="small" data-action="claim-confirm" data-id="${c.id}">Sim, vendi</ion-button>
        <ion-button size="small" fill="outline" color="medium" data-action="claim-contest" data-id="${c.id}">Não reconheço</ion-button>
      </div>
    </div>
  </div>`;

/** Dono provisório: comprovar com o CRLV-e no nome dele. */
function provisionalCard(claim) {
  const reviewing = claim?.status === 'PENDING_REVIEW';
  return html`
    <button type="button" class="panel row tappable provisional-card" data-action="proof">
      <span class="row-icon warning"><ion-icon name="${reviewing ? 'time-outline' : 'document-text-outline'}"></ion-icon></span>
      <div class="row-main">
        <span class="row-title">${reviewing ? 'Seu CRLV-e está em análise' : claim?.status === 'REJECTED' ? 'Documento recusado: envie de novo' : 'Envie o CRLV-e no seu nome'}</span>
        <span class="row-sub">${reviewing
          ? 'Quando aprovar, você deixa de ser dono provisório.'
          : 'Você recebeu o veículo pelo app. Quando o Detran emitir o documento no seu nome, envie para virar o dono.'}</span>
      </div>
      <ion-icon class="chevron" name="chevron-forward"></ion-icon>
    </button>`;
}

/** Dono comprovado: "Vendi este veículo" (ou a venda em andamento). */
function saleSection(sale) {
  if (!sale?.canSell) return '';
  const pending = sale.transfer?.status === 'PENDING' ? sale.transfer : null;
  return html`
    ${sectionTitle('Venda')}
    <div class="panel">
      <div class="row">
        <span class="row-icon ${pending ? 'warning' : ''}"><ion-icon name="${pending ? 'hourglass-outline' : 'swap-horizontal-outline'}"></ion-icon></span>
        <div class="row-main">
          <span class="row-title">${pending ? 'Venda em andamento' : 'Vendeu o veículo?'}</span>
          <span class="row-sub">${pending
            ? `Link de transferência ativo até ${fmtShort(pending.expiresAt)}. O veículo continua com você até o comprador aceitar.`
            : 'Passe para o comprador pelo app. O histórico de manutenções vai junto e valoriza a venda.'}</span>
        </div>
      </div>
      <div class="panel-foot"><ion-button expand="block" fill="outline" data-action="sell"><ion-icon slot="start" name="${pending ? 'qr-code-outline' : 'swap-horizontal-outline'}"></ion-icon>${pending ? 'Ver venda' : 'Vendi este veículo'}</ion-button></div>
    </div>`;
}

/** Quem é autorizado vê de quem é o veículo e o que pode fazer. */
const authorizedNotice = (owner) => html`
  <div class="panel notice">
    <span class="row-icon"><ion-icon name="people-outline"></ion-icon></span>
    <div>
      <strong>Você é pessoa autorizada${owner?.firstName ? html` · dono: ${owner.firstName}` : ''}</strong>
      <p>Você vê o histórico, recebe os lembretes e pode autorizar empresas. O veículo continua no nome do dono.</p>
      <ion-button size="small" fill="clear" color="medium" data-action="share-leave">Sair deste veículo</ion-button>
    </div>
  </div>`;

/** Dono: com quem o veículo está compartilhado (no máximo 1 pessoa). */
function shareSection(share) {
  if (!share) return '';
  const { authorized: a, invite: i } = share;
  return html`
    ${sectionTitle('Pessoa autorizada')}
    <div class="panel">
      ${a
        ? html`
          <div class="row">
            <span class="row-icon success"><ion-icon name="person-outline"></ion-icon></span>
            <div class="row-main">
              <span class="row-title">${a.name}</span>
              <span class="row-sub">${a.email} · desde ${fmt(a.since)}</span>
            </div>
          </div>
          <div class="panel-foot"><ion-button expand="block" fill="outline" color="danger" data-action="share-remove">Remover acesso</ion-button></div>`
        : i
          ? html`
            <div class="row">
              <span class="row-icon warning"><ion-icon name="mail-outline"></ion-icon></span>
              <div class="row-main">
                <span class="row-title">Convite enviado</span>
                <span class="row-sub">${i.email} · vale até ${fmt(i.expiresAt)}</span>
              </div>
            </div>
            <div class="panel-foot"><ion-button expand="block" fill="outline" color="medium" data-action="share-cancel">Cancelar convite</ion-button></div>`
          : html`
            <div class="row">
              <span class="row-icon"><ion-icon name="people-outline"></ion-icon></span>
              <div class="row-main">
                <span class="row-title">Ninguém além de você</span>
                <span class="row-sub">Quem dirige com você pode ver o histórico, receber os lembretes e autorizar empresas. O veículo continua no seu nome.</span>
              </div>
            </div>
            <div class="panel-foot"><ion-button expand="block" fill="outline" data-action="share-invite"><ion-icon slot="start" name="person-add-outline"></ion-icon>Compartilhar com uma pessoa</ion-button></div>`}
    </div>`;
}

function pending(claim, category = 'LIGHT') {
  if (!claim) return html`<p class="hint">Nenhum envio encontrado.</p>`;
  switch (claim.status) {
    case 'PENDING_UPLOAD':
      if (claim.documentReady) return html`${documentConfirmation(claim)}<ion-button expand="block" fill="clear" color="medium" data-action="cancel">Cancelar envio</ion-button>`;
      return html`
        <p class="hint lead">${category === 'AGRO' ? 'Falta enviar a nota fiscal (ou o CRLV) para comprovar que a máquina é sua.' : 'Falta enviar o CRLV-e para comprovar que o veículo é seu.'}</p>
        ${uploadBox({ category })}
        <ion-button expand="block" fill="clear" color="medium" data-action="cancel">Cancelar envio</ion-button>`;
    case 'PENDING_REVIEW':
      return html`
        <div class="panel notice">
          <span class="row-icon warning"><ion-icon name="time-outline"></ion-icon></span>
          <div>
            <strong>Documento em análise</strong>
            <p>Enviado em ${fmt(claim.uploadedAt)}. Nossa equipe confere se o CRLV-e está no seu nome; quando for aprovado, o veículo entra na sua garagem.</p>
          </div>
        </div>
        <div class="actions">
          <ion-button expand="block" fill="outline" data-action="view-file"><ion-icon slot="start" name="document-text-outline"></ion-icon>Ver documento enviado</ion-button>
          <ion-button expand="block" fill="clear" color="medium" data-action="cancel">Cancelar envio</ion-button>
        </div>`;
    case 'REJECTED':
      return html`
        <div class="panel notice danger">
          <span class="row-icon danger"><ion-icon name="alert-circle-outline"></ion-icon></span>
          <div>
            <strong>Documento recusado</strong>
            <p>${REJECTION_TEXT[claim.rejectionCode] ?? REJECTION_TEXT.OTHER}</p>
            ${claim.reason ? html`<p class="reason"><b>Observação:</b> ${claim.reason}</p>` : ''}
          </div>
        </div>
        <div class="actions">
          <ion-button expand="block" data-action="resubmit"><ion-icon slot="start" name="refresh-outline"></ion-icon>Enviar novo documento</ion-button>
          <ion-button expand="block" fill="clear" color="medium" data-action="dismiss">Remover da garagem</ion-button>
        </div>`;
    default:
      return html``;
  }
}

customElements.define('page-vehicle', VehiclePage);
